// src/services/storageService.ts
/**
 * Storage Service
 * 
 * Provides functionality for saving and loading game state and user profiles
 * from browser localStorage.
 */

// Types for game data
export interface GameState {
    unlocked: string[]; // Array of unlocked element IDs
    gauntletProgress: number; // Highest gauntlet level unlocked
    timeAttackHighScore: number;
    dailyPuzzlesCompleted: Record<string, number>; // Date string → star rating
    achievements: Record<string, boolean>; // Achievement ID → unlocked
    lastPlayed: number; // Timestamp
    // Daily streak (§5.2) — backward-compatible optional fields.
    streak?: number;
    streakLastDay?: string;   // "YYYY-M-D" of the last day activity was counted
    streakClaimedDay?: string; // "YYYY-M-D" the daily reward was last claimed
  }
  
  // Types for user profile
  export interface UserProfile {
    username: string;
    displayName: string;
    avatarUrl?: string;
    firebaseId?: string; // Added when connected to Firebase
    createdAt: number;
    preferences: {
      theme: 'dark' | 'light' | 'system';
      music: boolean;
      soundEffects: boolean;
      showHints: boolean;
    };
    progression?: {
      level: number;
      currentXP: number;
      totalXP: number;
      discoveryCount: number;
      combinationCount: number;
      achievements: Record<string, any>;
      milestones: string[];
    };
  }
  
  // Storage keys
  const STORAGE_KEYS = {
    GAME_STATE: 'alchemistry_gameState',
    USER_PROFILE: 'alchemistry_userProfile',
  };
  
  // Default game state
  const DEFAULT_GAME_STATE: GameState = {
    unlocked: [], // Will be populated with base elements by the store
    gauntletProgress: 1,
    timeAttackHighScore: 0,
    dailyPuzzlesCompleted: {},
    achievements: {},
    lastPlayed: Date.now(),
  };
  
  // Default user profile
  const DEFAULT_USER_PROFILE: UserProfile = {
    username: '',
    displayName: 'Alchemist',
    createdAt: Date.now(),
    preferences: {
      theme: 'dark',
      music: true,
      soundEffects: true,
      showHints: true,
    }
  };
  
  /**
   * Save game state to localStorage
   */
  export const saveGameState = (state: Partial<GameState>): void => {
    try {
      // Get existing state or use default
      const existingState = loadGameState();
      const updatedState = { ...existingState, ...state, lastPlayed: Date.now() };
      
      // Save to localStorage
      localStorage.setItem(STORAGE_KEYS.GAME_STATE, JSON.stringify(updatedState));
    } catch (error) {
      console.error('Failed to save game state:', error);
    }
  };
  
  /**
   * Load game state from localStorage
   */
  export const loadGameState = (): GameState => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEYS.GAME_STATE);
      if (!savedState) return DEFAULT_GAME_STATE;
      
      return { ...DEFAULT_GAME_STATE, ...JSON.parse(savedState) };
    } catch (error) {
      console.error('Failed to load game state:', error);
      return DEFAULT_GAME_STATE;
    }
  };
  
  /**
   * Save user profile to localStorage
   */
  export const saveUserProfile = (profile: Partial<UserProfile>): void => {
    try {
      // Get existing profile or use default
      const existingProfile = loadUserProfile();
      const updatedProfile = { ...existingProfile, ...profile };
      
      // Save to localStorage
      localStorage.setItem(STORAGE_KEYS.USER_PROFILE, JSON.stringify(updatedProfile));
    } catch (error) {
      console.error('Failed to save user profile:', error);
    }
  };
  
  /**
   * Load user profile from localStorage
   */
  export const loadUserProfile = (): UserProfile => {
    try {
      const savedProfile = localStorage.getItem(STORAGE_KEYS.USER_PROFILE);
      if (!savedProfile) return DEFAULT_USER_PROFILE;
      
      return { ...DEFAULT_USER_PROFILE, ...JSON.parse(savedProfile) };
    } catch (error) {
      console.error('Failed to load user profile:', error);
      return DEFAULT_USER_PROFILE;
    }
  };
  
  /**
   * Clear all saved data (for testing or reset)
   */
  export const clearAllData = (): void => {
    try {
      localStorage.removeItem(STORAGE_KEYS.GAME_STATE);
      localStorage.removeItem(STORAGE_KEYS.USER_PROFILE);
    } catch (error) {
      console.error('Failed to clear data:', error);
    }
  };
  
  /**
   * Export game data as JSON string for backup
   */
  export const exportGameData = (): string => {
    const data = {
      gameState: loadGameState(),
      userProfile: loadUserProfile(),
      exportedAt: new Date().toISOString(),
    };
    
    return JSON.stringify(data);
  };
  
  /**
   * Import game data from JSON string
   */
  export const importGameData = (jsonData: string): boolean => {
    try {
      const data = JSON.parse(jsonData);
      
      // Validate data structure
      if (!data.gameState || !data.userProfile) {
        throw new Error('Invalid data format');
      }
      
      // Save imported data
      localStorage.setItem(STORAGE_KEYS.GAME_STATE, JSON.stringify(data.gameState));
      localStorage.setItem(STORAGE_KEYS.USER_PROFILE, JSON.stringify(data.userProfile));
      
      return true;
    } catch (error) {
      console.error('Failed to import game data:', error);
      return false;
    }
  };